// Sammelaktionen für „Anfragen & Bestellungen“ (Loxpal, Sonstiges, Schweißen …):
// markierte Einträge → Anfrage an mehrere Lieferanten (Outlook-Entwurf), Bestellung als PDF.
// Gleicher Ablauf wie beim Blechbedarf: Anfrage → Bestellt → Wareneingang.
import { useState } from 'react';
import {
  api, type EinkaufSonstigesEintrag, type EinkaufSonstigesKategorie, type Lieferant,
} from '../lib/api';
import { MailModal, MAIL_SIGNATUR, oeffneOutlookEntwurf, type MailDraft } from '../components/MailEntwurf';
import { bestellPdfErzeugen, type BestellPdfDaten } from '../lib/bestellPdf';
import { anfrageText, emailVonLieferant, lieferantVonName } from './Blechbedarf';
import { gleicherLieferant, wunschVon } from '../lib/sonstigesListe';

// Jasmin 08.10.2026: Beim Schweißen brauchen wir immer Zeugnisse → fester Satz in Anfrage + Bestellung.
export const ZEUGNIS_HINWEIS = 'Bitte beachten Sie: Wir benötigen grundsätzlich Zeugnisse zu allen Artikeln.';
export const zusatzFuer = (kat: EinkaufSonstigesKategorie): string[] => (kat === 'schweissen' ? [ZEUGNIS_HINWEIS] : []);

const KAT_KURZ: Record<EinkaufSonstigesKategorie, string> = {
  loxpal: 'LOX', sonstiges: 'SON', projekte: 'PRO', schietinger: 'SCH', versand: 'VER', wuerth_flex: 'WFX', farbe: 'FAR', schweissen: 'SWE',
};
export const standardBestellnr = (kat: EinkaufSonstigesKategorie, datum = new Date()) =>
  `${KAT_KURZ[kat]}-${datum.toISOString().slice(0, 10).replace(/-/g, '')}`;

// „10 Spulen“ → Menge 10, ME „Spulen“; alles Unklare (z. B. „10 Spulen, 5 Spulen“) bleibt als Text in der Bemerkung
export function mengeSplit(stueck: string | null | undefined): { menge: string; einheit: string; rest: string } {
  const s = (stueck ?? '').trim();
  if (!s) return { menge: '—', einheit: '', rest: '' };
  const m = /^(\d+(?:[.,]\d+)?)\s*([A-Za-zÄÖÜäöüß.]{1,8})?$/.exec(s);
  if (m) return { menge: m[1] ?? s, einheit: m[2] ?? '', rest: '' };
  return { menge: '—', einheit: '', rest: s };
}

// Zeile je Position — bewusst ohne Referenz/Kommentar (intern)
export function anfrageZeile(r: Pick<EinkaufSonstigesEintrag, 'stueck' | 'was' | 'kg'>, nr: number): string {
  const teile = [r.stueck?.trim(), r.was.trim(), r.kg ? `${r.kg} kg` : ''].filter(Boolean);
  return `Pos. ${nr}: ${teile.join(' · ')}`;
}

export function sonstigesAnfrageMail(rows: EinkaufSonstigesEintrag[], kat: EinkaufSonstigesKategorie, email: string): MailDraft {
  return {
    an: email,
    betreff: `Anfrage – ${rows.length} Position${rows.length === 1 ? '' : 'en'}`,
    text: anfrageText(rows.map((r, i) => anfrageZeile(r, (i + 1) * 10)), zusatzFuer(kat)),
  };
}

export function sonstigesPdfDaten(rows: EinkaufSonstigesEintrag[], kat: EinkaufSonstigesKategorie,
  lieferant: Lieferant | undefined, name: string, bestellnr: string, liefertermin?: string): BestellPdfDaten {
  return {
    lieferant: lieferant?.name ?? name,
    ansprechpartner: lieferant?.ansprechpartner,
    bestellnr,
    liefertermin: liefertermin?.trim() || null,
    zusatzHinweise: zusatzFuer(kat),
    positionen: rows.map((r, i) => {
      const { menge, einheit, rest } = mengeSplit(r.stueck);
      return {
        nr: (i + 1) * 10, bezeichnung: r.was, menge, einheit,
        bemerkung: [rest ? `Menge: ${rest}` : '', r.kg ? `${r.kg} kg` : ''].filter(Boolean).join(' · ') || null,
        liefertermin: r.liefertermin,
      };
    }),
  };
}

export function sonstigesBestellMail(rows: EinkaufSonstigesEintrag[], kat: EinkaufSonstigesKategorie, bestellnr: string, email: string): MailDraft {
  return {
    an: email,
    betreff: `Bestellung ${bestellnr} – ${rows.length} Position${rows.length === 1 ? '' : 'en'}`,
    text: `Sehr geehrte Damen und Herren,\n\nanbei erhalten Sie unsere Bestellung Nr. ${bestellnr} (${rows.length} Position${rows.length === 1 ? '' : 'en'}) als PDF.\n\n` +
      'Bitte senden Sie uns eine Auftragsbestätigung mit verbindlichem Liefertermin.' +
      (zusatzFuer(kat).length ? '\n' + zusatzFuer(kat).join('\n') : '') +
      '\n\nVielen Dank.\n\n' + MAIL_SIGNATUR,
  };
}

const Rahmen = ({ titel, onClose, children }: { titel: string; onClose: () => void; children: React.ReactNode }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">{titel}</h2>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none" aria-label="Schließen">×</button>
      </div>
      {children}
    </div>
  </div>
);

const Positionsliste = ({ rows }: { rows: EinkaufSonstigesEintrag[] }) => (
  <ul className="text-sm border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-48 overflow-y-auto">
    {rows.map((r, i) => <li key={r.id} className="px-3 py-1.5"><span className="text-slate-400 mr-2">{(i + 1) * 10}</span>{r.stueck ? `${r.stueck} · ` : ''}{r.was}{wunschVon(r) && <span className="ml-2 text-xs text-teal-700">→ {wunschVon(r)}</span>}</li>)}
  </ul>
);

// ── Anfrage an einen oder mehrere Lieferanten ─────────────────────────────────
export function AnfrageModal({ rows: zeilen, kategorie, lieferanten, onClose, onFertig }: {
  rows: EinkaufSonstigesEintrag[]; kategorie: EinkaufSonstigesKategorie; lieferanten: Lieferant[];
  onClose: () => void; onFertig: () => void;
}) {
  const [rows] = useState(zeilen);          // Momentaufnahme: die Markierung wird nach dem Speichern geleert
  // Vorbelegung: Wunschlieferanten aus der Liste („von Novofil“)
  const [namen, setNamen] = useState<string[]>(() => {
    const l: string[] = [];
    for (const r of zeilen) { const w = wunschVon(r); if (w && !l.some(x => gleicherLieferant(x, w))) l.push(w); }
    return l;
  });
  const [eingabe, setEingabe] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [fertig, setFertig] = useState<{ entwuerfe: number; ohneEmail: string[] } | null>(null);

  const hinzu = () => {
    const n = eingabe.trim();
    if (n && !namen.some(x => x.toLowerCase() === n.toLowerCase())) setNamen(l => [...l, n]);
    setEingabe('');
  };

  // Je Lieferant nur die eigenen Positionen: Wunschlieferant passt, oder kein Wunsch, oder der Wunsch ist nicht unter den gewählten
  const zeilenFuer = (n: string) => rows.filter(r => {
    const w = wunschVon(r);
    return !w || gleicherLieferant(w, n) || !namen.some(x => gleicherLieferant(x, w));
  });

  async function erzeugen() {
    if (namen.length === 0) return;
    setBusy(true); setFehler('');
    try {
      // 1) „Angefragt bei“ nachtragen (je Eintrag nur die Lieferanten, die diese Position wirklich bekommen)
      for (const r of rows) {
        const bisher = (r.angefragt_bei ?? '').trim();
        const neu = namen.filter(n => zeilenFuer(n).includes(r) && !bisher.toLowerCase().includes(n.toLowerCase()));
        if (neu.length) await api.einkaufSonstiges.update(r.id, { angefragt_bei: [bisher, neu.join(', ')].filter(Boolean).join(', ') });
      }
      // 2) je Lieferant ein Outlook-Entwurf
      const ohneEmail: string[] = [];
      for (const [i, n] of namen.entries()) {
        const email = emailVonLieferant(lieferanten, n);
        if (!email) ohneEmail.push(n);
        oeffneOutlookEntwurf(sonstigesAnfrageMail(zeilenFuer(n), kategorie, email));
        if (i < namen.length - 1) await new Promise(res => setTimeout(res, 400));
      }
      setFertig({ entwuerfe: namen.length, ohneEmail });
      onFertig();
    } catch (e) { setFehler((e as Error).message); }
    setBusy(false);
  }

  return (
    <Rahmen titel={`✉ Anfrage — ${rows.length} Position${rows.length === 1 ? '' : 'en'}`} onClose={onClose}>
      {fertig ? (
        <div className="p-6 space-y-3" data-testid="anfrage-fertig">
          <div className="text-emerald-800 font-semibold">✅ {fertig.entwuerfe} Outlook-Entwurf{fertig.entwuerfe === 1 ? '' : 'e'} erstellt — „Angefragt bei“ ist eingetragen.</div>
          <p className="text-sm text-slate-600">Die .eml-Dateien doppelklicken, in Outlook prüfen und abschicken — es wird nichts automatisch versendet.</p>
          {fertig.ohneEmail.length > 0 && <p className="text-sm text-amber-700">Keine E-Mail im Lieferantenstamm: {fertig.ohneEmail.join(', ')} — Adresse in Outlook ergänzen.</p>}
          <div className="flex justify-end"><button className="btn-secondary" onClick={onClose}>Schließen</button></div>
        </div>
      ) : (
        <>
          <div className="p-6 space-y-4">
            <Positionsliste rows={rows} />
            {kategorie === 'schweissen' && <div className="text-xs text-slate-500" data-testid="zeugnis-hinweis">Der Text enthält automatisch: „{ZEUGNIS_HINWEIS}“</div>}
            <div>
              <label className="block text-xs text-slate-500 mb-1">Lieferant(en) — für jeden wird ein eigener Entwurf erstellt</label>
              <datalist id="so-lieferanten">{lieferanten.map(l => <option key={l.id} value={l.name} />)}</datalist>
              <div className="flex gap-2">
                <input className="input flex-1" list="so-lieferanten" aria-label="Lieferant hinzufügen" placeholder="Lieferant …" value={eingabe}
                       onChange={e => setEingabe(e.target.value)}
                       onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); hinzu(); } }} />
                <button className="btn-secondary" onClick={hinzu} disabled={!eingabe.trim()}>＋ Hinzufügen</button>
              </div>
              <div className="flex gap-1.5 flex-wrap mt-2" data-testid="anfrage-namen">
                {namen.map(n => (
                  <span key={n} className="inline-flex items-center gap-1 rounded-full bg-teal-100 text-teal-900 text-xs font-medium px-2.5 py-1">
                    {n} <span className="text-teal-700/70">({zeilenFuer(n).length})</span>{!lieferantVonName(lieferanten, n) && <span title="nicht im Lieferantenstamm"> ⚠</span>}
                    <button aria-label={`${n} entfernen`} onClick={() => setNamen(l => l.filter(x => x !== n))} className="text-teal-700 hover:text-red-600">×</button>
                  </span>
                ))}
              </div>
            </div>
            {fehler && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{fehler}</div>}
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
            <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
            <button onClick={() => void erzeugen()} disabled={busy || namen.length === 0} data-testid="anfrage-erzeugen"
                    className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">
              {busy ? 'Erstellt …' : `📧 ${namen.length || ''} Outlook-Entwurf${namen.length === 1 ? '' : 'e'} erstellen`}
            </button>
          </div>
        </>
      )}
    </Rahmen>
  );
}

// ── Bestellung (PDF) ──────────────────────────────────────────────────────────
export function BestellModal({ rows: zeilen, kategorie, lieferanten, onClose, onFertig }: {
  rows: EinkaufSonstigesEintrag[]; kategorie: EinkaufSonstigesKategorie; lieferanten: Lieferant[];
  onClose: () => void; onFertig: () => void;
}) {
  const [rows] = useState(zeilen);          // Momentaufnahme: die Markierung wird nach dem Speichern geleert
  // Vorbelegung: alle markierten Einträge wurden bei genau einem Lieferanten angefragt
  const gemeinsam = (() => {
    const s = new Set(rows.map(r => (r.angefragt_bei ?? '').trim() || wunschVon(r)));
    return s.size === 1 ? ([...s][0] ?? '') : '';
  })();
  const [lieferant, setLieferant] = useState(gemeinsam.includes(',') ? '' : gemeinsam);
  const [bestellnr, setBestellnr] = useState(standardBestellnr(kategorie));
  const [termin, setTermin] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [gespeichert, setGespeichert] = useState(false);
  const [mail, setMail] = useState<MailDraft | null>(null);

  const lf = lieferantVonName(lieferanten, lieferant);

  async function bestellen() {
    if (!lieferant.trim() || !bestellnr.trim()) return;
    setBusy(true); setFehler('');
    try {
      for (const r of rows) {
        await api.einkaufSonstiges.update(r.id, { bestellt_bei: lieferant.trim(), ...(termin.trim() ? { liefertermin: termin.trim() } : {}) });
      }
      setGespeichert(true);
      onFertig();
    } catch (e) { setFehler((e as Error).message); }
    setBusy(false);
  }
  const rowsMitTermin = termin.trim() ? rows.map(r => ({ ...r, liefertermin: termin.trim() })) : rows;

  return (
    <>
      <Rahmen titel={`📦 Bestellung — ${rows.length} Position${rows.length === 1 ? '' : 'en'}`} onClose={onClose}>
        <div className="p-6 space-y-4">
          <Positionsliste rows={rows} />
          {kategorie === 'schweissen' && <div className="text-xs text-slate-500" data-testid="zeugnis-hinweis">PDF und Mail enthalten automatisch: „{ZEUGNIS_HINWEIS}“</div>}
          <datalist id="so-best-lieferanten">{lieferanten.map(l => <option key={l.id} value={l.name} />)}</datalist>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="text-xs text-slate-500">Lieferant *
              <input className="input w-full mt-1" list="so-best-lieferanten" aria-label="Bestellt bei" value={lieferant} disabled={gespeichert}
                     onChange={e => setLieferant(e.target.value)} /></label>
            <label className="text-xs text-slate-500">Bestellnummer *
              <input className="input w-full mt-1" aria-label="Bestellnummer" value={bestellnr} disabled={gespeichert}
                     onChange={e => setBestellnr(e.target.value)} /></label>
            <label className="text-xs text-slate-500">Liefertermin (optional)
              <input className="input w-full mt-1" aria-label="Liefertermin" placeholder="z. B. KW 44" value={termin} disabled={gespeichert}
                     onChange={e => setTermin(e.target.value)} /></label>
          </div>
          {lieferant.trim() && !lf && <div className="text-xs text-amber-700">„{lieferant.trim()}“ ist nicht im Lieferantenstamm — ohne E-Mail-Adresse kann der Entwurf nicht adressiert werden.</div>}
          {fehler && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{fehler}</div>}
          {gespeichert && <div className="text-emerald-800 font-semibold" data-testid="bestell-fertig">✅ Bestellt gespeichert. Jetzt die Bestellung als PDF/Mail erzeugen — es wird nichts automatisch versendet.</div>}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>{gespeichert ? 'Schließen' : 'Abbrechen'}</button>
          {!gespeichert ? (
            <button onClick={() => void bestellen()} disabled={busy || !lieferant.trim() || !bestellnr.trim()} data-testid="bestellen-ok"
                    className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-40">
              {busy ? 'Speichert …' : 'Als bestellt markieren'}
            </button>
          ) : (
            <button data-testid="bestell-mail" className="bg-brand text-white rounded-lg px-5 py-2 text-sm font-medium hover:opacity-90"
                    onClick={() => setMail(sonstigesBestellMail(rows, kategorie, bestellnr.trim(), emailVonLieferant(lieferanten, lieferant)))}>
              ✉ Bestellung (PDF / Mail)
            </button>
          )}
        </div>
      </Rahmen>
      {mail && <MailModal draft={mail} onChange={setMail} onClose={() => setMail(null)} titel="✉ Bestellung an Lieferant"
                          pdf={() => bestellPdfErzeugen(sonstigesPdfDaten(rowsMitTermin, kategorie, lf, lieferant.trim(), bestellnr.trim(), termin))} />}
    </>
  );
}
